import React, { useEffect, useRef, useState } from "react";
import { getRequest, postRequest } from "../../apis/ApiInstence";

const PENDING_TXN_KEY = "phonepe_pending_txn";

const RenderPhonePe = ({
  amount = 0,
  currency = "INR",
  userId = "guest",
  customerEmail,
  customerMobile,
  disabled = false,
  setPaymentOption,
  forceSuccess = false
}) => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [showErrorBar, setShowErrorBar] = useState(false);
  const hasVerifiedRef = useRef(false);

  const getPendingTxn = () => {
    try {
      const raw = localStorage.getItem(PENDING_TXN_KEY);
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      if (!parsed?.id) return "";
      const isFresh = Date.now() - Number(parsed.ts || 0) < 30 * 60 * 1000;
      return isFresh ? parsed.id : "";
    } catch {
      return "";
    }
  };

  const clearPendingTxn = () => {
    localStorage.removeItem(PENDING_TXN_KEY);
  };

  const showApiError = (fallbackMsg, err) => {
    const msg = err?.response?.data?.message || err?.message || fallbackMsg;
    setErrorMsg(msg);
    setShowErrorBar(true);
    setTimeout(() => setShowErrorBar(false), 3000);
  };

  const isPaymentSuccess = (statusData) => {
    const state = String(statusData?.data?.state || "").toUpperCase().trim();
    const responseCode = String(statusData?.data?.responseCode || "").toUpperCase().trim();
    return state === "COMPLETED" && responseCode === "SUCCESS";
  };

  const verifyStatus = async (transactionId) => {
    if (!transactionId) return;
    if (hasVerifiedRef.current) return;

    hasVerifiedRef.current = true;
    setVerifying(true);
    setErrorMsg("");

    try {
      const statusRes = await getRequest(
        `payment/status/${encodeURIComponent(transactionId)}`
      );
      const statusData = statusRes?.data || {};

      if (isPaymentSuccess(statusData)) {
        try {
          await postRequest("payment/makepayment", {
            payment_id: transactionId,
            user_id: userId || "guest",
            status: "success",
            details: statusData
          });
        } catch {
          // Do not block order placement if payment log API fails.
        }
        clearPendingTxn();
        if (typeof setPaymentOption === "function") {
          setPaymentOption(statusData);
        }
      } else {
        try {
          await postRequest("payment/makepayment", {
            payment_id: transactionId,
            user_id: userId || "guest",
            status: "failed",
            details: statusData
          });
        } catch {
          // no-op
        }
        setErrorMsg("Payment not completed. Please try again.");
      }
    } catch (err) {
      showApiError("Payment verification failed.", err);
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasPhonePeReturnSignal =
      params.has("transactionId") ||
      params.has("merchantTransactionId") ||
      params.has("txnId") ||
      params.has("code") ||
      params.has("state");

    if (!hasPhonePeReturnSignal) return;

    const txnFromUrl =
      params.get("transactionId") ||
      params.get("merchantTransactionId") ||
      params.get("txnId") ||
      "";

    const transactionId = txnFromUrl || getPendingTxn();
    if (transactionId) {
      verifyStatus(transactionId);
    } else {
      showApiError("Transaction ID missing after PhonePe redirect.", {});
    }
  }, []);

  const handlePay = async () => {
    if (disabled || loading) return;
    if (!amount || Number.isNaN(Number(amount))) return;

    setLoading(true);
    setErrorMsg("");

    try {
      if (forceSuccess) {
        setTimeout(() => {
          setLoading(false);
          if (typeof setPaymentOption === "function") {
            setPaymentOption({
              data: { state: "COMPLETED", responseCode: "SUCCESS" },
              mocked: true
            });
          }
        }, 600);
        return;
      }

      const checkoutRedirectUrl = `${window.location.origin}/checkout`;
      const tokenRes = await postRequest("payment/getToken", {
        amount: Number(amount),
        user_id: userId || "guest",
        mobile: customerMobile || "",
        orderId: `ORD_${Date.now()}`,
        redirectUrl: checkoutRedirectUrl
      });

      const tokenData = tokenRes?.data || {};

      const redirectUrl =
        tokenData?.data?.redirectUrl ||
        tokenData?.redirectUrl ||
        tokenData?.instrumentResponse?.redirectInfo?.url ||
        tokenData?.data?.instrumentResponse?.redirectInfo?.url;

      const merchantTransactionId =
        tokenData?.merchantTransactionId ||
        tokenData?.data?.merchantTransactionId;

      if (!redirectUrl) {
        throw new Error("Invalid PhonePe redirect URL.");
      }

      if (merchantTransactionId) {
        localStorage.setItem(
          PENDING_TXN_KEY,
          JSON.stringify({ id: merchantTransactionId, ts: Date.now() })
        );
      }

      window.location.href = redirectUrl;
    } catch (err) {
      showApiError("PhonePe payment failed to start.", err);
      setLoading(false);
    }
  };

  const displayAmount =
    currency === "INR" ? `\u20B9${Number(amount).toFixed(2)}` : amount;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <button
        type="button"
        onClick={handlePay}
        disabled={loading || verifying || disabled}
        className="mt-3 rounded-lg bg-[#1F3C88] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
      >
        {loading ? "Redirecting to PhonePe..." : verifying ? "Verifying payment..." : `Pay ${displayAmount} via PhonePe`}
      </button>

      {errorMsg && (
        <p className="mt-3 text-xs text-red-600">{errorMsg}</p>
      )}

      {showErrorBar && errorMsg && (
        <div className="fixed top-4 right-4 z-50 rounded-lg bg-red-600 px-4 py-2 text-xs font-medium text-white shadow-lg">
          {errorMsg}
        </div>
      )}
    </div>
  );
};

export default RenderPhonePe;
